/**
 * useActividadData — adapter entre el back real y el contrato de la vista
 * Actividad. Toma los repartidores + paquetes del día desde el store
 * `delivered` (mismo origen que /admin/live-tracking y /admin/live-routes) y
 * los emparenta con las posiciones GPS del socket; devuelve un arreglo de
 * `RutaActividad` listo para que la UI no sepa que vino del back legacy.
 *
 * Qué queda pendiente del back y por eso usa placeholder:
 *  - device.senal/bateriaPct/discoPct → null (chip se muestra como “—”)
 *  - chatNoLeidos / mensajes → 0/[] (chat usa empty-state explicativo)
 *  - patente / vehiculoLabel / agrupacion → del driver si vienen; si no, “—”
 *  - subestado / monto / productos / fueraHorario → del paquete si vienen
 *
 * El día que el back exponga el endpoint nativo de Actividad, sustituir este
 * hook por un fetch directo al contrato — la UI no necesita cambios.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useAuth from '../../../hooks/useAuth';
import useAuthScope from '../../../hooks/useAuthScope';
import useLiveRoutesPolling from '../../../hooks/useLiveRoutesPolling';
import useResyncOnVisible from '../../../hooks/useResyncOnVisible';
import useDeliveredStore from '../../../store/delivered';
import { localDateStr } from '../../../utils/formatters';
import { getOperationTimezone } from '../../../utils/timezone';
import { locationSocketService } from '../../../services/locationSocketService';
import { AuthService } from '../../../services/authservice';
import { filterPackagesByOperatingDay } from '../../../pages/liveRoutes/utils/dayBoundary';

const SNAPSHOT_MS = 8000;

// ── Adaptadores puros ───────────────────────────────────────────────────────

function statusToParadaEstado(status) {
  if (status === 4) return 'entregada';
  if (status === 6 || status === 7) return 'no_entregada';
  if (status === 5) return 'no_entregada'; // cancelado: lo agrupamos con no_entregada
  if (status === 3 || status === 30) return 'pendiente';
  return 'pendiente';
}

function pkgTimeOfEvent(pkg) {
  if (pkg.status === 4) return pkg.deliveredAt || pkg.lastStatusChange || null;
  if (pkg.status === 6 || pkg.status === 7) return pkg.lastFailedAt || pkg.lastStatusChange || null;
  return pkg.lastStatusChange || null;
}

// "Ahora" cuantizado a 5 minutos para los placeholders de hora estimada: si
// cada adaptación usara new Date() a secas, TODA parada pendiente cambiaría de
// valor (y de identidad) en cada corrida — anulando cualquier memoización aguas
// abajo y re-pintando timelines sin que nada real haya cambiado.
const NOW_QUANTUM_MS = 5 * 60 * 1000;
function quantizedNow() {
  return new Date(Math.floor(Date.now() / NOW_QUANTUM_MS) * NOW_QUANTUM_MS);
}

function pkgEstimatedTime(pkg, idx, totalPending) {
  // El back actual no expone hora estimada por parada. Como placeholder hasta
  // que exista: para paradas ya gestionadas usamos su evento real; para
  // pendientes las repartimos uniformemente entre "ahora" y el final del día
  // operativo (21h), para que el timeline las muestre alineadas.
  if (pkg.status === 4 || pkg.status === 6 || pkg.status === 7) {
    return pkgTimeOfEvent(pkg);
  }
  if (pkg.estimatedDeliveryAt) return pkg.estimatedDeliveryAt;
  if (pkg.scheduledAt) return pkg.scheduledAt;
  // Fallback: spread linealmente desde ahora (cuantizado) hasta 21:00 según índice.
  const now = quantizedNow();
  const endOfDay = new Date(now);
  endOfDay.setHours(21, 0, 0, 0);
  const remainingMs = Math.max(endOfDay.getTime() - now.getTime(), 30 * 60 * 1000);
  const slot = totalPending > 1 ? (remainingMs / totalPending) * idx : remainingMs / 2;
  return new Date(now.getTime() + slot).toISOString();
}

function initials(name) {
  if (!name) return '··';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '··';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function pickFirst(...candidates) {
  for (const c of candidates) {
    if (c != null && c !== '') return c;
  }
  return null;
}

/**
 * Bloque de pago contra entrega de un paquete del payload agrupado, o `null`.
 *
 * La señal de "este pedido lleva cobro" es `amount > 0`, no un flag: el backend
 * omite el subdocumento entero cuando no hay cobro. `collectedAmount` puede
 * diferir de `amount` (el repartidor registra lo efectivamente cobrado), y por
 * eso viajan los dos: el pendiente se calcula sobre `amount` y lo cobrado sobre
 * `collectedAmount`.
 */
function codBlock(pkg) {
  const amount = Number(pkg.cod_amount);
  if (!Number.isFinite(amount) || amount <= 0) return null;

  const collected = Number(pkg.cod_collected_amount);
  return {
    amount,
    // El backend siempre estampa `pending` al crear; el default sólo cubre un
    // documento histórico anterior al campo.
    status: pkg.cod_status || 'pending',
    method: pkg.cod_method || null,
    collectedAmount: Number.isFinite(collected) ? collected : null,
    currency: pkg.cod_currency || null,
  };
}

function adaptPackageToParada(pkg, idx, totalPending) {
  const estado = statusToParadaEstado(pkg.status);
  const gestionada = pkg.status === 4 || pkg.status === 5 || pkg.status === 6 || pkg.status === 7;
  // horaGestion = hora real del EVENTO de gestión (entrega/fallo). Una parada
  // pendiente NO tiene evento: antes caía a lastStatusChange (la hora del
  // escaneo/carga) y el front la mostraba como si fuera hora de entrega.
  const horaGestion = gestionada ? pkgTimeOfEvent(pkg) : null;
  const horaEstimada = pkgEstimatedTime(pkg, idx, totalPending);
  // etaReal: la hora estimada es una ETA de verdad (backend/rutificador), no el
  // spread placeholder. El timeline y el detalle ocultan la "hora" de pendientes
  // sin ETA real (hasta que la ruta esté optimizada no hay hora programada).
  const etaReal = gestionada || Boolean(pkg.estimatedDeliveryAt || pkg.scheduledAt);

  // El endpoint /routes/packagesGroupedByDeliverer devuelve campos FLAT
  // (no shipping_address anidado). Lo que sí trae:
  //   - _id (del paquete), followingNumber (tracking), address_line, city_name
  //   - status, lastStatusChange, deliveredAt, lastFailedAt
  //   - failed_option / failed_description (nota del conductor en fallidas)
  //   - realReceiver_name / realReceiver_rut (solo cuando status=4 entregada)
  //   - shipping_latitude/longitude (planeado) y coords_latitude/longitude (capturado)
  //   - type (origen del pedido: MERCADOLIBRE, SHOPIFY, OTS, EXCEL...)
  //   - cod_amount / cod_status / cod_method / cod_collected_amount / cod_currency
  //     (pago contra entrega; null en todo paquete sin cobro)
  // Pedidos en ruta (status=3) NO traen nombre de cliente — la app móvil
  // solo lo captura al entregar. Mantenemos compat con shipping_address
  // anidado por si en algún rol/holding viene así.
  const sa = pkg.shipping_address || {};

  const cliente = pickFirst(
    pkg.realReceiver_name,                    // nombre real al entregar
    pkg.recipient_name,                       // legacy
    pkg.client_name,
    sa.recipient_name,
    sa.contact_name,
    sa.first_name && `${sa.first_name} ${sa.last_name || ''}`.trim(),
  ) || null;

  const direccion = pickFirst(
    pkg.address_line,
    sa.address_line,
    sa.street_name && sa.street_number
      ? `${sa.street_name} ${sa.street_number}`
      : sa.street_name,
  ) || '—';

  // El back actual entrega `city_name` como string flat (a veces es comuna
  // real, a veces "Región Metropolitana" cuando no llegó comuna).
  const comuna = pickFirst(
    pkg.city_name,
    sa.municipality?.name,
    sa.city?.name,
    sa.commune,
  ) || '';

  const subestado = pickFirst(
    pkg.failureReason,
    pkg.failedReason,
    pkg.partialReason,
    pkg.lastNote,
  );

  // Coordenadas para el mapa. coords_* = donde el conductor marcó la entrega
  // (más precisas, solo si entregado); shipping_* = dirección planeada.
  // El back puede entregarlas como número o como string, de ahí la coerción; si
  // el valor no es una coordenada numérica utilizable (`"N/A"`, texto suelto),
  // `Number(...)` da NaN y el mapa terminaría haciendo flyTo a LatLng(NaN, NaN),
  // que deja el viewport inservible. Preferimos descartar el punto entero.
  const lat = Number(pkg.coords_latitude ?? pkg.shipping_latitude ?? sa.latitude);
  const lng = Number(pkg.coords_longitude ?? pkg.shipping_longitude ?? sa.longitude);
  const geo = (Number.isFinite(lat) && Number.isFinite(lng)) ? { lat, lng } : null;

  // Receptor real (solo aparece en status=4): si tenemos rut/nombre lo
  // exponemos para que el detail panel muestre el bloque "Receptor".
  const receptor = (pkg.realReceiver_name || pkg.realReceiver_rut)
    ? {
        nombre: pkg.realReceiver_name || undefined,
        rut: pkg.realReceiver_rut || undefined,
        relacion: undefined,
      }
    : undefined;

  // Telefono y email de cliente — opcionales (placeholder si no vienen).
  const telefonoCliente = pickFirst(pkg.recipient_phone, sa.phone, sa.contact_phone) || undefined;
  const emailCliente = pickFirst(pkg.recipient_email, sa.email) || undefined;

  const numero = pkg.followingNumber || pkg.tracking_number || pkg._id?.slice(-6) || '';

  return {
    id: pkg._id || `pkg-${numero}-${idx}`,
    // `pkgId` = _id real del paquete, separado de `id` (identidad de selección
    // en la UI, que puede ser sintética). Es lo que deja abrir el detalle con
    // `GET /packages/:id`; sin él el panel cae a buscar por followingNumber.
    pkgId: pkg._id ? String(pkg._id) : null,
    numero: String(numero),
    // Origen del pedido (`package.type`): MERCADOLIBRE, SHOPIFY, OTS, EXCEL...
    // Se llama `origen` y no `tipo` a propósito: `ruta.tipo` ya significa otra
    // cosa en este modelo (troncal vs. última milla). El casing viene sucio de
    // la colección y hay documentos históricos sin el campo; ambos casos los
    // resuelve `<OriginIcon>` al pintar, no acá.
    origen: pkg.type || null,
    // Pago contra entrega. Se expone como UN bloque y no como cinco campos
    // sueltos para que `Boolean(parada.cod)` sea la pregunta "¿este pedido lleva
    // cobro?" — que es la que hacen el filtro y el resumen. `null` cuando no hay
    // cobro: un pedido sin COD no guarda el subdocumento (no lo guarda en cero),
    // así que un monto 0 o ausente es exactamente lo mismo.
    cod: codBlock(pkg),
    // El back de packagesGroupedByDeliverer NO entrega nombre del receptor
    // hasta que se entrega el paquete (`realReceiver_name` aparece en
    // status=4). Dejamos `null` cuando no hay nombre — los componentes
    // resuelven el fallback visual (lista usa dirección, detail muestra
    // "Receptor pendiente").
    cliente: cliente || null,
    identificador: pkg.client_external_id || undefined,
    telefonoCliente,
    emailCliente,
    direccion,
    comuna,
    geo,
    estado,
    subestado: subestado || undefined,
    horaEstimada: horaEstimada || quantizedNow().toISOString(),
    horaGestion: horaGestion || undefined,
    etaReal,
    fueraHorario: Boolean(pkg.outOfWindow || pkg.fueraHorario),
    bultos: pkg.quantity ?? pkg.boxes ?? pkg.packages_count ?? 1,
    monto: pkg.declaredValue ?? pkg.amount ?? undefined,
    productos: pickFirst(pkg.product, pkg.description, pkg.productName) || undefined,
    // Nota del conductor de la gestión fallida, ya resuelta por el back
    // (`failed` con fallback al último `failedAttempts`). Ambos `undefined`
    // contra un backend que aún no los expone.
    motivoFallo: pkg.failed_option || undefined,
    notaConductor: pkg.failed_description || undefined,
    // Pending del back — placeholders que la UI maneja con "—" / empty-state.
    productosDetalle: undefined,
    receptor,
    evidencias: undefined,
    comentarios: undefined,
    intentos: pkg.attempts ?? pkg.deliveryAttempts ?? 1,
  };
}

function deriveRouteEstado(packages) {
  if (!packages || packages.length === 0) return 'no_iniciada';
  let inRoute = 0, done = 0, pending = 0;
  for (const p of packages) {
    if (p.status === 3 || p.status === 30) inRoute++;
    else if (p.status === 4 || p.status === 6 || p.status === 7 || p.status === 5) done++;
    else pending++;
  }
  if (inRoute > 0) return 'en_ruta';
  if (done > 0 && pending === 0) return 'finalizada';
  return 'no_iniciada';
}

function adaptDriverToRuta(driver, location, dateLabel, tz, t) {
  // Solo las gestiones del día operativo: el back arrastra en la ruta del
  // repartidor entregas/fallidos de días anteriores. Los "en ruta" (status
  // 3/30) se mantienen siempre; entregados/fallidos/cancelados solo si su
  // último cambio de estado cae en `dateLabel` (borde de día a las 2am).
  const packages = filterPackagesByOperatingDay(driver.packages || [], dateLabel, tz);
  const estado = deriveRouteEstado(packages);

  // Paradas ordenadas:
  //   · gestionadas (entregadas/fallidas/canceladas) ASC por hora real del evento;
  //   · pendientes (en_ruta) ordenadas por `followingNumber` ascendente para que
  //     el orden sea estable y previsible — el back devuelve los pendientes en
  //     orden arbitrario (mismo lastStatusChange porque vinieron en batch), y
  //     dejarlos "tal cual" hacía que se vieran salteados en el timeline.
  const gestionadas = [];
  const pendientes = [];
  for (const p of packages) {
    if (p.status === 4 || p.status === 6 || p.status === 7 || p.status === 5) {
      gestionadas.push(p);
    } else {
      pendientes.push(p);
    }
  }
  gestionadas.sort((a, b) => {
    const ta = new Date(pkgTimeOfEvent(a) || 0).getTime();
    const tb = new Date(pkgTimeOfEvent(b) || 0).getTime();
    return ta - tb;
  });
  pendientes.sort((a, b) => {
    const na = Number(a.followingNumber) || 0;
    const nb = Number(b.followingNumber) || 0;
    return na - nb;
  });

  const ordered = [...gestionadas, ...pendientes];
  const totalPending = pendientes.length;
  // Ahora pendientes ya están en orden estable: pendingIdx == su posición.
  const paradas = ordered.map((p, i) => {
    if (p.status === 3 || p.status === 30 || p.status === 0 || p.status === 1 || p.status === 2) {
      const pendingIdx = pendientes.indexOf(p);
      return adaptPackageToParada(p, pendingIdx, totalPending);
    }
    return adaptPackageToParada(p, i, totalPending);
  });

  const iniciadaA = gestionadas.length > 0
    ? pkgTimeOfEvent(gestionadas[0])
    : undefined;
  const finalizadaA = estado === 'finalizada' && gestionadas.length > 0
    ? pkgTimeOfEvent(gestionadas[gestionadas.length - 1])
    : undefined;

  const name = driver.delivererFullName || driver.name || t('Sin nombre');
  const patente = pickFirst(driver.licensePlate, driver.plate, driver.vehiclePlate) || '—';
  const vehiculoLabel = pickFirst(
    driver.vehicleType,
    driver.vehicleModel,
    driver.vehicle,
  ) || '';
  const agrupacion = pickFirst(driver.zone, driver.officeName, driver.branch) || t('Sin agrupación');

  // Code corto y estable: fecha + 6 últimos del id del driver.
  const idTail = (driver._id || '').toString().slice(-6).toUpperCase() || 'XXXXXX';
  const rutaCodigo = `R-${dateLabel}-${idTail}`;

  // GPS encendido si el socket reporta el driver con una location reciente.
  // Señal/batería/disco llegan en location.device (emitidos por la app móvil);
  // si el equipo no reporta alguna métrica, queda null y la UI la muestra "—".
  const gpsOn = Boolean(location && location.latitude != null && location.longitude != null);
  const dev = location?.device ?? {};
  const posicionActual = gpsOn
    ? { lat: Number(location.latitude), lng: Number(location.longitude) }
    : undefined;

  // Haystack de búsqueda precalculado UNA vez por adaptación: la vista filtra
  // por texto en cada tecla y antes hacía toLowerCase de patente/nombre/código
  // + cada número de parada por ruta en cada pasada.
  const searchHaystack = [
    patente, name, rutaCodigo,
    ...paradas.map((p) => p.numero),
  ].join(' ').toLowerCase();

  return {
    id: driver._id || `drv-${idTail}`,
    rutaCodigo,
    // Día operativo y timezone de ESTA ruta. Van en el objeto porque los
    // consumidores del panel solo reciben `ruta`, y sin ellos tienen que
    // adivinar la ventana temporal del recorrido GPS (que no es el día natural
    // del navegador: ver `getOperatingDayRange`).
    fecha: dateLabel,
    timezone: tz,
    patente,
    vehiculoLabel,
    transportistaId: driver._id || idTail,
    transportistaNombre: name,
    transportistaTelefono: driver.phone || driver.delivererPhone || '',
    avatarInitials: initials(name),
    tipo: 'ultima_milla', // El back no diferencia troncal vs última milla aún.
    agrupacion,
    estado,
    iniciadaA,
    finalizadaA,
    device: {
      gps: gpsOn,
      senal: dev.senal ?? null,
      bateriaPct: dev.bateriaPct ?? null,
      discoPct: dev.discoPct ?? null,
      // Detalle extendido (tab Telemetría); los chips solo usan los de arriba.
      redGeneracion: dev.redGeneracion ?? null,
      senalDbm: dev.senalDbm ?? null,
      bateriaEstado: dev.bateriaEstado ?? null,
      discoLibreBytes: dev.discoLibreBytes ?? null,
      discoTotalBytes: dev.discoTotalBytes ?? null,
      deviceModel: dev.deviceModel ?? null,
    },
    paradas,
    chatNoLeidos: 0, // Hasta que exista el back de chat.
    // Telemetría espacial — el back todavía no expone breadcrumbs históricos
    // del conductor. La posición actual sí (vía socket).
    recorridoGps: [],
    posicionActual,
    // Bitácora de la app móvil — pendiente del back.
    eventos: [],
    // Métricas operacionales — pendientes del back.
    kmRecorridos: null,
    kmPlanificados: null,
    tiempoTotalMin: null,
    tiempoConduciendoMin: null,
    tiempoEnParadasMin: null,
    searchHaystack,
  };
}

// ── Hook ────────────────────────────────────────────────────────────────────

export default function useActividadData({ date: dateOverride, logisticCompanyId: lcidOverride } = {}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const scope = useAuthScope();
  // `lcidOverride` es un fallback opcional: solo se usa cuando el usuario/scope
  // no traen empresa logística propia (p.ej. el superadmin del sistema, que no
  // está atado a un tenant). Los callers actuales no lo pasan ⇒ sin cambios.
  const logisticCompanyId = user?.logisticCompanyId || scope.logisticCompanyId || lcidOverride;
  const companyId = scope.companyId;
  const isCommerceFleet = user?.company?.hasOwnFleet && !logisticCompanyId;
  const timezone = useMemo(() => getOperationTimezone(user), [user]);
  const today = useMemo(() => localDateStr(new Date(), timezone), [timezone]);
  const date = dateOverride || today;

  // Polling de rutas/paquetes del día (mismo hook que LiveTracking/LiveRoutes).
  const { refetch, lastUpdated } = useLiveRoutesPolling({
    date,
    logisticCompanyId: isCommerceFleet ? undefined : logisticCompanyId,
    companyId: isCommerceFleet ? companyId : undefined,
    timezone,
  });
  const delivered = useDeliveredStore((s) => s.delivered[date]);
  // Último fetch fallido (null si el último fue OK) — para que la vista pueda
  // avisar que está mostrando datos viejos en vez de fingir frescura.
  const fetchError = useDeliveredStore((s) => s.fetchError);

  // Snapshot de posiciones GPS (driverId → location), refrescado vía socket.
  const positionsRef = useRef(new Map());
  // dirty = llegó al menos un update de posición desde el último flush. Sin
  // esto, el flush de 8s emitía un Map nuevo (⇒ re-render + re-adaptación de
  // TODAS las rutas) aunque no hubiera llegado ningún GPS.
  const dirtyRef = useRef(false);
  const [snapshot, setSnapshot] = useState(new Map());
  const [gpsConnected, setGpsConnected] = useState(false);

  // Foto completa de posiciones por REST: al montar y al volver de un rato con la
  // pestaña oculta (el socket solo trae cambios y puede haberse cortado entretanto).
  const loadPositions = useCallback(async () => {
    try {
      const locs = await locationSocketService.fetchLocationsREST();
      if (Array.isArray(locs)) {
        locs.forEach(({ driverId, location }) => {
          if (driverId && location) positionsRef.current.set(driverId, location);
        });
        setSnapshot(new Map(positionsRef.current));
      }
    } catch {
      /* la vista sigue mostrando datos del día aunque no haya GPS */
    }
  }, []);

  useResyncOnVisible(
    useCallback(() => {
      if (!AuthService.hasSession()) return;
      locationSocketService.resume();
      loadPositions();
    }, [loadPositions]),
  );

  useEffect(() => {
    if (!AuthService.hasSession()) return undefined;
    const adminId = localStorage.getItem('adminId') || 'admin';

    loadPositions();

    locationSocketService.connect(adminId);
    const unsubConn = locationSocketService.onConnectionChange(setGpsConnected);
    const unsubLoc = locationSocketService.onLocationUpdate(({ driverId, location }) => {
      if (!driverId || !location) return;
      positionsRef.current.set(driverId, location);
      dirtyRef.current = true;
    });

    const flush = setInterval(() => {
      if (!document.hidden && dirtyRef.current) {
        dirtyRef.current = false;
        setSnapshot(new Map(positionsRef.current));
      }
    }, SNAPSHOT_MS);

    return () => {
      clearInterval(flush);
      unsubConn();
      unsubLoc();
      locationSocketService.disconnect();
    };
  }, [loadPositions]);

  // Cache de adaptación por driver: adaptDriverToRuta es O(paradas) (filtro de
  // día, dos sorts y map completo) y el flush GPS de 8s regenera `snapshot`
  // aunque solo un conductor se haya movido. Con la cache, un driver se
  // re-adapta SOLO si cambió su payload (identidad nueva tras un poll con
  // cambios) o su location; el resto conserva identidad de objeto — que es lo
  // que permite a React.memo de RouteCard saltarse el re-render.
  const adaptCacheRef = useRef(new Map());
  const lastRutasRef = useRef(null);

  const rutas = useMemo(() => {
    if (delivered === undefined) {
      lastRutasRef.current = null;
      return null; // loading
    }
    // esHoy: la vista puede mirar fechas pasadas; los consumidores "en vivo"
    // (ruta planificada, etc.) solo aplican sobre el día operativo actual.
    const esHoy = date === today;
    const cache = adaptCacheRef.current;
    const nextCache = new Map();
    let anyNew = false;
    const arr = (delivered || []).map((d, idx) => {
      const key = d._id ?? `i${idx}`;
      const location = snapshot.get(d._id);
      const hit = cache.get(key);
      if (
        hit && hit.driver === d && hit.location === location &&
        hit.date === date && hit.timezone === timezone && hit.ruta.esHoy === esHoy
      ) {
        nextCache.set(key, hit);
        return hit.ruta;
      }
      anyNew = true;
      const ruta = { ...adaptDriverToRuta(d, location, date, timezone, t), esHoy };
      nextCache.set(key, { driver: d, location, date, timezone, ruta });
      return ruta;
    });
    adaptCacheRef.current = nextCache;
    // Si ningún driver cambió (p.ej. flush GPS que solo movió drivers de otra
    // fecha), conservar también la identidad del ARREGLO: los memos aguas abajo
    // (KPIs, filtro, sort) tampoco recomputan.
    const prev = lastRutasRef.current;
    if (!anyNew && prev && prev.length === arr.length) return prev;
    lastRutasRef.current = arr;
    return arr;
  }, [delivered, snapshot, date, timezone, today]);

  const loading = delivered === undefined;

  const fetchChat = useCallback(async () => {
    // Hasta que el back exponga endpoint de chat por ruta, devolvemos lista
    // vacía — la UI muestra un empty-state explicativo.
    return [];
  }, []);

  return { rutas, loading, refetch, lastUpdated, gpsConnected, fetchError, fetchChat, date, timezone };
}
